import { ClientKillSwitchProvider, defaultClientKillSwitchConfig } from '@cbhq/client-kill-switch';
import type { GraphqlEnvironment } from '@cbhq/data-layer';
import type { ErrorMetadata } from '@cbhq/error-vitals-web';
import {
  ClientContextProvider as ErrorVitalsClientContextProvider,
  ErrorBoundary,
} from '@cbhq/error-vitals-web';
import { CookieBanner } from '@cbhq/ucpm-web';

import { BasicErrorFallback } from './components/ErrorBoundary/BasicErrorFallback';
import { PrefetchErrorAssets } from './components/ErrorBoundary/PrefetchErrorAssets';
import { Navigation } from './components/Navigation';
import { ucpmEnvironment } from './configs/environments';
import { getErrorVitalsClient } from './utils/errorVitals';
import { killswitchConfig } from './utils/killSwitches/initKillSwitches';
import { AppProviders } from './AppProviders';
import { Routes } from './Routes';

const TOP_LEVEL_ERROR_METADATA: ErrorMetadata = {
  context: 'top_level_boundary',
  isHandled: false,
  severity: 'crash',
  isUserVisible: true,
};

function getErrorVitalsValue() {
  return { client: getErrorVitalsClient() };
}

export type AppProps = {
  locale: string;
  messages?: Record<string, string>;
  environment: GraphqlEnvironment;
};

export function App({ locale, messages, environment }: AppProps) {
  return (
    <ClientKillSwitchProvider
      initialKillSwitches={defaultClientKillSwitchConfig}
      scope={killswitchConfig.scope}
    >
      <CookieBanner product="coinbase" environment={ucpmEnvironment} locale={locale} />
      <PrefetchErrorAssets />
      <ErrorVitalsClientContextProvider value={getErrorVitalsValue()}>
        <ErrorBoundary
          name="top-level-boundary"
          defaultMetadata={TOP_LEVEL_ERROR_METADATA}
          fallback={<BasicErrorFallback />}
        >
          <AppProviders locale={locale} messages={messages} environment={environment}>
            <Navigation>
              <Routes />
            </Navigation>
          </AppProviders>
        </ErrorBoundary>
      </ErrorVitalsClientContextProvider>
    </ClientKillSwitchProvider>
  );
}
